import { ContentCentralSetup } from './ContentCentralSetup';
import { NucleusOneSetup } from './NucleusOneSetup';
import type { PaigeDestinationsBridge } from './paige.global';
import type { FieldsProps } from './DestinationFields';

/** What a product's fields need beside the draft: Paige's bridge (the stored destination is the draft's). */
export interface ProductFieldsProps extends FieldsProps {
  bridge: PaigeDestinationsBridge;
}

/** The write-only key a product issued; stored once, then "Stored; type to replace". */
function KeyField({
  label,
  placeholder,
  draft,
  set,
  secretSet,
}: FieldsProps & { label: string; placeholder: string }) {
  return (
    <label>
      {label}
      <input
        type="password"
        value={draft.apiKey}
        onChange={set('apiKey')}
        placeholder={secretSet ? 'Stored; type to replace' : placeholder}
        required={!secretSet}
        autoComplete="new-password"
      />
    </label>
  );
}

/**
 * A Content Central destination, as CapturePoint's Connect · Catalog · Match: the site and the integration key
 * first (Add stores them, sealed); then, on the stored destination, the catalog picked from what the key can see
 * or created from this job, the type map from the catalog's own types and fields, and Check with Content Central.
 */
export function ContentCentralFields(props: ProductFieldsProps) {
  const { draft, set, bridge, destinationId } = props;
  return (
    <>
      <label>
        Site URL
        <input
          type="url"
          value={draft.siteUrl}
          onChange={set('siteUrl')}
          placeholder="https://documents.example.com/ContentCentral"
          required
        />
      </label>
      <KeyField {...props} label="Integration key" placeholder="ccapi_..." />
      {destinationId ? (
        <ContentCentralSetup {...props} bridge={bridge} destinationId={destinationId} />
      ) : (
        <small>
          In Content Central: Administration › API Connections › Paige › create a connection, then
          copy its key here. Paige keeps it sealed; the catalog comes next.
        </small>
      )}
    </>
  );
}

/**
 * A Nucleus One destination, as CapturePoint's Nucleus One card: the API key first (Add stores it, sealed); then,
 * on the stored destination, the organization › project › folder picker, the classification and the field map with
 * the project's own fields offered, and where the job's latest document would be filed.
 */
export function NucleusOneFields(props: ProductFieldsProps) {
  const { bridge, destinationId } = props;
  return (
    <>
      <KeyField {...props} label="API key" placeholder="" />
      {destinationId ? (
        <NucleusOneSetup {...props} bridge={bridge} destinationId={destinationId} />
      ) : (
        <small>
          In Nucleus One: your picture › API Keys › Add API Key, then copy it here. Paige keeps it
          sealed; the project comes next.
        </small>
      )}
    </>
  );
}
